"use client";

import * as React from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import {
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../shadcn/ui";
import {
  getCountriesWithFlags,
  type CountryWithFlag,
} from "../config/country.config";
import { cn } from "../lib/utils";

type Variant = "dropdown" | "list";

export interface CountryPickerProps {
  value: string | string[];
  onValueChange: (value: string | string[]) => void;
  variant?: Variant;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  triggerClassName?: string;
  /** Dropdown only: extra classes on the search row wrapper (icon + input). */
  commandInputWrapperClassName?: string;
  /** Dropdown only: extra classes on the search input. */
  commandInputClassName?: string;
  /** Dropdown only: extra classes on popover container. */
  popoverContentClassName?: string;
  /** Dropdown only: extra classes on the country list container. */
  commandListClassName?: string;
  /** Dropdown only: extra classes on each country item row. */
  commandItemClassName?: string;
  /** Dropdown only: stop wheel event bubbling to parent scroll containers. */
  stopListScrollPropagation?: boolean;
  countries?: CountryWithFlag[];
}

export function CountryPicker({
  value,
  onValueChange,
  variant = "dropdown",
  placeholder = "Select country",
  disabled = false,
  className,
  triggerClassName,
  commandInputWrapperClassName,
  commandInputClassName,
  popoverContentClassName,
  commandListClassName,
  commandItemClassName,
  stopListScrollPropagation = false,
  countries: countriesProp,
}: CountryPickerProps) {
  const [open, setOpen] = React.useState(false);
  const [searchValue, setSearchValue] = React.useState("");
  const popoverContentRef = React.useRef<HTMLDivElement | null>(null);
  const countries = countriesProp ?? getCountriesWithFlags();

  const isMulti = variant === "list";

  // Normalize value
  const selectedValues = React.useMemo(
    () => (Array.isArray(value) ? value : value ? [value] : []),
    [value]
  );

  const selectedSingle = countries.find((c) => c.name === value);

  const filteredCountries = React.useMemo(() => {
    const query = searchValue.trim().toLowerCase();
    if (!query) {
      return countries;
    }

    return [...countries]
      .filter((country) => country.name.toLowerCase().includes(query))
      .sort((a, b) => {
        const aName = a.name.toLowerCase();
        const bName = b.name.toLowerCase();
        const aStartsWith = aName.startsWith(query) ? 1 : 0;
        const bStartsWith = bName.startsWith(query) ? 1 : 0;

        if (aStartsWith !== bStartsWith) {
          return bStartsWith - aStartsWith;
        }

        const aExact = aName === query ? 1 : 0;
        const bExact = bName === query ? 1 : 0;
        if (aExact !== bExact) {
          return bExact - aExact;
        }

        return aName.localeCompare(bName);
      });
  }, [countries, searchValue]);

  React.useEffect(() => {
    if (!open) {
      setSearchValue("");
      return;
    }

    const commandList = popoverContentRef.current?.querySelector<HTMLElement>(
      '[data-slot="command-list"]'
    );
    if (commandList) {
      commandList.scrollTop = 0;
    }
  }, [open, searchValue]);

  // Toggle logic
  const toggleCountry = (countryName: string) => {
    if (!isMulti) {
      onValueChange(countryName);
      setOpen(false);
      return;
    }

    if (selectedValues.includes(countryName)) {
      onValueChange(selectedValues.filter((c) => c !== countryName));
    } else {
      onValueChange([...selectedValues, countryName]);
    }
  };

// =========================
// ✅ LIST VARIANT (SHOW MORE + SEARCH)
// =========================
if (variant === "list") {
  const [search, setSearch] = React.useState("");
  const [showAll, setShowAll] = React.useState(false);

  const visibleCountries = React.useMemo(() => {
    let list = countries;

    // Apply search ONLY when expanded
    if (showAll && search.trim()) {
      const q = search.toLowerCase();
      list = countries.filter((c) =>
        c.name.toLowerCase().includes(q)
      );
    }

    // Limit initial view
    return showAll ? list : list.slice(0, 7);
  }, [countries, search, showAll]);

  return (
    <div className={cn("space-y-2", className)}>
      {/* 🔍 Search (ONLY when expanded) */}
      {showAll && (
        <div className="relative">
          <input
            type="search"
            placeholder="Search country..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full h-9 px-3 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-gray-300"
          />
        </div>
      )}

      {/* 📋 List */}
      <div className="space-y-2 max-h-[230px] overflow-auto pr-1">
        {visibleCountries.length === 0 ? (
          <p className="text-xs text-gray-500 py-2">
            No country found.
          </p>
        ) : (
          visibleCountries.map((country) => {
            const isSelected = selectedValues.includes(country.name);

            return (
              <label
                key={country.code}
                className="flex items-center gap-2 cursor-pointer"
                onClick={() => toggleCountry(country.name)}
              >
                {/* Checkbox */}
                <div
                  className={cn(
                    "h-4 w-4 border rounded flex items-center justify-center",
                    isSelected
                      ? "bg-black border-black"
                      : "border-gray-300"
                  )}
                >
                  {isSelected && (
                    <Check className="h-3 w-3 text-white" />
                  )}
                </div>

                <span>{country.flag}</span>
                <span className="text-sm text-gray-700 truncate">
                  {country.name}
                </span>
              </label>
            );
          })
        )}
      </div>

      {/* 🔽 Show More / Less */}
      {countries.length > 8 && (
        <button
          type="button"
          className="text-sm font-medium text-slate-600 hover:text-slate-900"
          onClick={() => {
            setShowAll((prev) => {
              if (prev) {
                setSearch(""); // reset search when collapsing
              }
              return !prev;
            });
          }}
        >
          {showAll ? "Show less" : "Show more"}
        </button>
      )}
    </div>
  );
}

  // =========================
  // ✅ DROPDOWN VARIANT (DEFAULT)
  // =========================
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "w-full justify-between font-normal h-10 border-gray-300 rounded bg-white hover:bg-gray-50",
            triggerClassName,
            className
          )}
        >
          <span className="truncate">
            {selectedSingle
              ? `${selectedSingle.flag} ${selectedSingle.name}`
              : placeholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>

      <PopoverContent
        ref={popoverContentRef}
        className={cn(
          "w-[var(--radix-popover-trigger-width)] min-w-[200px] p-0 border border-gray-200 shadow-md bg-white",
          popoverContentClassName
        )}
        align="start"
      >
        <Command className="rounded-lg border-0 bg-white" shouldFilter={false}>
          <CommandInput
            placeholder="Search country..."
            value={searchValue}
            onValueChange={setSearchValue}
            wrapperClassName={commandInputWrapperClassName}
            className={cn(
              "h-9 border-0 px-2 active:border-orange-500 focus:border-orange-500 focus-visible:border-orange-500 focus:outline-none focus-visible:outline-none focus-visible:ring-0",
              commandInputClassName,
            )}
          />

          <CommandList
            className={cn(
              "max-h-[300px] overflow-y-auto overscroll-contain",
              commandListClassName
            )}
            onWheelCapture={(event) => {
              if (stopListScrollPropagation) {
                event.stopPropagation();
              }
            }}
          >
            <CommandEmpty>No country found.</CommandEmpty>

            <CommandGroup>
              {filteredCountries.map((country) => (
                <CommandItem
                  key={country.code}
                  value={country.name}
                  className={cn("cursor-pointer", commandItemClassName)}
                  onSelect={() => toggleCountry(country.name)}
                >
                  <span className="mr-2">{country.flag}</span>
                  <span className="flex-1 truncate">
                    {country.name}
                  </span>
                  <Check
                    className={cn(
                      "ml-auto h-4 w-4 shrink-0",
                      value === country.name
                        ? "opacity-100"
                        : "opacity-0"
                    )}
                  />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}